import { useQuery } from '@tanstack/react-query';

import { adminGetJson, type AdminChatTranscriptPage } from './adminApi';

/**
 * GET /api/admin/v2/workflows… — the "User workflows" viewer (superadmin, or
 * the `workflows_read` capability). Read-only; every open is audited server
 * side. Money is `credits` (charged, net) and `usd` (provider cost, net).
 */
export interface WorkflowListItem {
  pipeline_id: string;
  name: string | null;
  /** null for a system template. */
  profile_id: string | null;
  email: string | null;
  user_name: string | null;
  workspace_id: string | null;
  runs: number;
  last_run_at: string | null;
  generation_credits: number;
  generation_usd: number;
  llm_credits: number;
  llm_usd: number;
  total_credits: number;
  total_usd: number;
}

export interface WorkflowListPage {
  total: number;
  items: WorkflowListItem[];
}

export type WorkflowSort = 'spend' | 'recent';

export interface WorkflowSpend {
  generation_credits: number;
  generation_usd: number;
  llm_credits: number;
  llm_usd: number;
  total_credits: number;
  total_usd: number;
}

export interface WorkflowNodeRun {
  node_run_id: string;
  instance_id: string;
  status: string | null;
  error_message: string | null;
  /** Whatever the node produced: media keys / URLs, text, or structured JSON. */
  output: unknown;
  credits: number;
  usd: number;
  provider: string | null;
  model: string | null;
}

export interface WorkflowRun {
  id: string;
  status: string | null;
  date_created: string | null;
  date_finished: string | null;
  credits: number;
  usd: number;
  /** The graph as it was when this run started. */
  graph_snapshot: unknown;
  nodes: WorkflowNodeRun[];
}

export interface WorkflowChatRef {
  id: string;
  title: string | null;
  date_created: string | null;
  message_count: number;
}

export interface WorkflowDetail {
  pipeline: {
    id: string;
    name: string | null;
    graph: unknown;
    /** null for a system template. */
    profile_id: string | null;
    email: string | null;
    user_name: string | null;
    workspace_id: string | null;
    date_created: string | null;
    date_updated: string | null;
  };
  spend: WorkflowSpend;
  runs: WorkflowRun[];
  chats: WorkflowChatRef[];
}

/** Every open is audited server side: no background refetch, five minutes fresh. */
const STALE_MS = 5 * 60_000;

export function workflowsListPath(p: {
  q?: string;
  sort?: WorkflowSort;
  limit: number;
  offset: number;
}): string {
  const qs = new URLSearchParams();
  if (p.q) qs.set('q', p.q);
  if (p.sort) qs.set('sort', p.sort);
  qs.set('limit', String(p.limit));
  qs.set('offset', String(p.offset));
  return `/api/admin/v2/workflows?${qs.toString()}`;
}

export function useWorkflowList(
  params: { q: string; sort: WorkflowSort; limit: number; offset: number },
  enabled: boolean,
) {
  return useQuery<WorkflowListPage>({
    queryKey: ['admin-workflows', 'list', params],
    queryFn: ({ signal }) => adminGetJson<WorkflowListPage>(workflowsListPath(params), { signal }),
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: STALE_MS,
    placeholderData: (previous) => previous,
  });
}

export function useWorkflowDetail(pipelineId: string | undefined, enabled: boolean) {
  return useQuery<WorkflowDetail>({
    queryKey: ['admin-workflows', 'detail', pipelineId],
    queryFn: ({ signal }) =>
      adminGetJson<WorkflowDetail>(
        `/api/admin/v2/workflows/${encodeURIComponent(pipelineId as string)}`,
        { signal },
      ),
    enabled: enabled && Boolean(pipelineId),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: STALE_MS,
  });
}

/** One chat of the workflow, same transcript shape as the per-user chat. */
export function useWorkflowChat(
  pipelineId: string | undefined,
  chatId: string | null,
  cursor: string | null,
) {
  return useQuery<AdminChatTranscriptPage>({
    queryKey: ['admin-workflows', 'chat', pipelineId, chatId, cursor],
    queryFn: () => {
      const qs = cursor ? `?after=${encodeURIComponent(cursor)}` : '';
      return adminGetJson<AdminChatTranscriptPage>(
        `/api/admin/v2/workflows/${encodeURIComponent(pipelineId as string)}/chats/${encodeURIComponent(chatId as string)}${qs}`,
      );
    },
    enabled: Boolean(pipelineId) && Boolean(chatId),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: STALE_MS,
    placeholderData: (previous) => previous,
  });
}
